import { useEffect, useMemo, useState } from 'react'
import DOMPurify from 'dompurify'
import { ExternalLink, Key, ShieldAlert } from 'lucide-react'
import { api, type DarkWebCredentialStatus } from '../../../lib/api'
import { useToast } from '../../../components/Toast'
import { Button, Drawer, EmptyState, ErrorBanner, Pagination, StatusChip, Table, type Column, type StatusTone } from '../../../components/ui'
import { useDarkWebPage } from '../DarkWebContext'
import { STATUS_LABEL, STATUS_TONE, formatDay } from '../format'

type Breach = Awaited<ReturnType<typeof api.getDarkWebBreaches>>['breaches'][number]

const PAGE_SIZE = 50
const SEVERITY_TONE: Record<string, StatusTone> = { critical: 'critical', high: 'serious', medium: 'warning', low: 'neutral' }
const SEVERITY_LABEL: Record<string, string> = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' }
const field = 'h-[38px] rounded-lg border border-line-strong bg-surface px-3 text-sm text-ink focus:outline-none focus-visible:ring-[3px] focus-visible:ring-focus'

// HIBP descriptions contain links; keep only links and emphasis
function sanitizeDescription(html: string): string {
  return DOMPurify.sanitize(html || '', { ALLOWED_TAGS: ['a', 'b', 'strong', 'em', 'i'], ALLOWED_ATTR: ['href'] })
}

/**
 * Breaches tab (#397 step 3d): HIBP breaches that include your employees, with remediation of
 * the affected employees from the breach drawer.
 */
export function BreachesTab() {
  const toast = useToast()
  const { canRemediate, dataVersion, refreshStats } = useDarkWebPage()
  const [breaches, setBreaches] = useState<Breach[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [severity, setSeverity] = useState('')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [hideResolved, setHideResolved] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<Breach | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => setOffset(0), [severity, debouncedSearch, hideResolved])

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const res = await api.getDarkWebBreaches({
        severity: (severity || undefined) as 'critical' | undefined,
        search: debouncedSearch || undefined,
        sort: 'added_date',
        hideResolved,
        limit: PAGE_SIZE,
        offset,
      })
      if (res.success) {
        setBreaches(res.breaches)
        setTotal(res.total)
        setOpen(prev => (prev ? res.breaches.find(b => b.name === prev.name) || null : prev))
      } else {
        setError((res as { error?: string }).error || 'The server returned an error.')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Network error.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [severity, debouncedSearch, hideResolved, offset, dataVersion])

  useEffect(() => setSelected(new Set()), [open?.name])

  async function updateSelected(status: DarkWebCredentialStatus, notes?: string) {
    if (!open) return
    const ids = [...selected].map(email => open.affectedEmailStatus?.[email]?.id).filter(Boolean) as string[]
    if (ids.length === 0) return
    setBusy(true)
    try {
      const res = await api.updateDarkWebCredentials(ids, { status, notes })
      if (!res.success) throw new Error(res.error || res.message || 'Update failed')
      toast.success(`${ids.length} ${ids.length === 1 ? 'employee' : 'employees'}: ${STATUS_LABEL[status].toLowerCase()}`)
      setSelected(new Set())
      await Promise.all([load(), refreshStats()])
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update status')
    } finally {
      setBusy(false)
    }
  }

  const columns: Array<Column<Breach>> = [
    {
      key: 'name',
      header: 'Breach',
      cell: b => (
        <button type="button" onClick={() => setOpen(b)} className="flex flex-col items-start gap-0.5 rounded text-left focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-focus">
          <span className="font-semibold text-ink hover:underline">{b.title || b.name}</span>
          {b.domain && <span className="text-xs text-muted">{b.domain}</span>}
        </button>
      ),
    },
    { key: 'breachDate', header: 'Breach date', cell: b => <span className="whitespace-nowrap text-ink-secondary">{formatDay(b.breachDate ? Date.parse(b.breachDate) : null)}</span> },
    {
      key: 'data',
      header: 'Data exposed',
      cell: b => (
        <span className="text-ink-secondary">
          {b.hasPassword && <span className="mr-1.5 inline-flex items-center gap-1 font-medium text-critical"><Key className="h-3 w-3" aria-hidden="true" />Passwords</span>}
          {b.dataClasses.filter(dc => !/password/i.test(dc)).slice(0, 2).join(', ')}
          {b.dataClasses.length > 3 && <span className="text-muted"> +{b.dataClasses.length - 3}</span>}
        </span>
      ),
    },
    { key: 'employees', header: 'Employees', align: 'right', cell: b => <span className="tabular-nums">{b.affectedEmails.length.toLocaleString()}</span> },
    { key: 'severity', header: 'Severity', cell: b => <StatusChip tone={SEVERITY_TONE[b.severity]}>{SEVERITY_LABEL[b.severity]}</StatusChip> },
  ]

  const openEmails = open?.affectedEmails || []
  const selectableEmails = useMemo(() => openEmails.filter(e => open?.affectedEmailStatus?.[e]?.id), [open])
  const allSelected = selectableEmails.length > 0 && selectableEmails.every(e => selected.has(e))

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-[220px] flex-1 flex-col gap-1.5">
          <label htmlFor="dw-breach-search" className="text-[13px] font-medium text-ink-secondary">Search breaches</label>
          <input id="dw-breach-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Name, domain or data type" className={field} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="dw-breach-severity" className="text-[13px] font-medium text-ink-secondary">Severity</label>
          <select id="dw-breach-severity" value={severity} onChange={e => setSeverity(e.target.value)} className={field}>
            <option value="">All severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        <label className="flex h-[38px] items-center gap-2 text-[13px] text-ink-secondary">
          <input type="checkbox" checked={hideResolved} onChange={e => setHideResolved(e.target.checked)} className="h-4 w-4 accent-brand" />
          Hide resolved
        </label>
      </div>

      {error && <ErrorBanner title="Couldn't load breaches" description={`${error} This does not mean no exposures were found.`} onRetry={load} />}

      {!error && (
        <Table
          caption="Breaches affecting your employees"
          columns={columns}
          rows={breaches}
          rowKey={b => b.name}
          loading={loading}
          minWidth={760}
          empty={<EmptyState icon={<ShieldAlert className="h-8 w-8" />} title="No breaches to show" description={hideResolved ? 'Breaches where every employee is handled are hidden.' : undefined} />}
          footer={<Pagination offset={offset} limit={PAGE_SIZE} total={total} onChange={setOffset} noun="breaches" />}
        />
      )}

      <Drawer
        open={!!open}
        onClose={() => setOpen(null)}
        title={open?.title || open?.name || ''}
        description={open && (
          <span className="flex flex-wrap items-center gap-2">
            <StatusChip tone={SEVERITY_TONE[open.severity]}>{SEVERITY_LABEL[open.severity]}</StatusChip>
            <span>Breached {formatDay(open.breachDate ? Date.parse(open.breachDate) : null)} · {(open.pwnCount || 0).toLocaleString()} accounts overall</span>
          </span>
        )}
        footer={canRemediate && selected.size > 0 && (
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            <span className="mr-auto text-sm font-semibold">{selected.size} selected</span>
            <Button size="sm" disabled={busy} onClick={() => updateSelected('false_positive')}>False positive</Button>
            <Button size="sm" disabled={busy} onClick={() => updateSelected('reset_requested')}>Mark reset requested</Button>
            <Button size="sm" variant="primary" loading={busy} onClick={() => updateSelected('resolved', 'Password reset done')}>Password reset done</Button>
          </div>
        )}
      >
        {open && (
          <div className="flex flex-col gap-4 text-sm">
            <p className="m-0 text-ink-secondary [&_a]:text-link [&_a]:underline" dangerouslySetInnerHTML={{ __html: sanitizeDescription(open.description) }} />
            <div>
              <span className="font-semibold">Data exposed</span>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {open.dataClasses.map(dc => (
                  <span key={dc} className={`rounded-md px-2 py-0.5 text-xs ${open.criticalTypes?.includes(dc) ? 'bg-critical-tint font-medium text-critical' : 'bg-surface-raised text-ink-secondary'}`}>{dc}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">Your employees in this breach ({openEmails.length})</span>
                {canRemediate && selectableEmails.length > 0 && (
                  <button type="button" onClick={() => setSelected(allSelected ? new Set() : new Set(selectableEmails))} className="rounded text-[13px] text-link hover:underline focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-focus">
                    {allSelected ? 'Deselect all' : 'Select all'}
                  </button>
                )}
              </div>
              <ul className="m-0 mt-2 flex list-none flex-col gap-1 p-0">
                {openEmails.map(email => {
                  const credential = open.affectedEmailStatus?.[email]
                  const status = credential?.status || 'new'
                  return (
                    <li key={email} className="flex items-center justify-between gap-2 rounded-lg border border-line px-3 py-2">
                      <label className="flex min-w-0 items-center gap-2">
                        {canRemediate && credential && (
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-brand"
                            checked={selected.has(email)}
                            onChange={() => setSelected(prev => {
                              const next = new Set(prev)
                              if (next.has(email)) next.delete(email)
                              else next.add(email)
                              return next
                            })}
                          />
                        )}
                        <span className="truncate font-mono text-[12.5px]">{email}</span>
                      </label>
                      <StatusChip tone={STATUS_TONE[status] || 'critical'}>{STATUS_LABEL[status] || status}</StatusChip>
                    </li>
                  )
                })}
              </ul>
              <p className="mt-2 text-xs text-muted">Status covers each employee's exposure across all their breaches.</p>
            </div>
            <a href={`https://haveibeenpwned.com/PwnedWebsites#${encodeURIComponent(open.name)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-link hover:underline">
              View on Have I Been Pwned <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          </div>
        )}
      </Drawer>
    </div>
  )
}
